import * as React from 'react'
import { FormEvent, ReactElement, useMemo } from 'react'
import { CommandBar, CommandBarButton, ICommandBarItemProps, Separator, Stack, StackItem, Text, TextField } from '@fluentui/react'
import { IUseBooleanCallbacks, useBoolean } from '@fluentui/react-hooks'
import nl2br from 'react-nl2br'
import { formatFullDateTime } from '../../helpers/dateHelper'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch, Store } from '../../reducers'
import { ShortUserListItemDto } from '../../config/userConfig'
import { NoteAction, NoteMutationDto, NoteOverviewDto } from '../../config/noteConfig'
import { Formik, FormikContextType, FormikHelpers, useFormikContext } from 'formik'
import * as Yup from 'yup'
import Loading from '../../components/Loading'
import { IFileDto } from '../../config/fileConfig'
import useUser from '../../hooks/useUser'
import useAxios from '../../hooks/useAxios'
import { AxiosInstance } from 'axios'
import DialogButton from '../../components/Button/DialogButton'
import DeleteDialog, { DeleteDialogProps } from '../../components/Dialog/LegacyDeleteDialog'
import FormattedDateTime from '../Date/FormattedDateTime'
import { useTranslation } from 'react-i18next'
import PersonAvatar from '../PersonAvatar'

type Props = NoteOverviewDto & {
    file?: IFileDto
}

type ContainerProps = {
    note: NoteOverviewDto
    editProps: [boolean, IUseBooleanCallbacks]
}

type ContentProps = {
    edit: boolean
    setEdit: () => void
}

type RevisionTitleProps = {
    isNew: boolean
    author: ShortUserListItemDto
    date: string
}

type CommandBarItem = ICommandBarItemProps & { visible: boolean }
type CommandBarItems = {
    items: CommandBarItem[]
    farItems: CommandBarItem[]
}

const validationSchema = Yup.object().shape({
    content: Yup.string(),
})

const getCommandBarItems = (
    context: FormikContextType<NoteMutationDto>,
    dispatch: Dispatch,
    axios: AxiosInstance,
    translate: (key: string, options?: any) => string,
    { note, editProps: [edit, { setTrue: setEdit, setFalse: unsetEdit }] }: ContainerProps,
    isAuthor: boolean,
): CommandBarItems => ({
    items: [
        {
            visible: edit,
            key: 'cancel-note',
            iconProps: { iconName: 'Cancel' },
            iconOnly: true,
            title: translate('action.cancel'),
            disabled: context.isSubmitting,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                context.setFieldValue('content', note.content)
                unsetEdit()
            },
        },
        {
            visible: note.id && !edit,
            key: 'delete-note',
            title: translate('action.delete'),
            onRender: () => (
                <DialogButton<DeleteDialogProps>
                    button={CommandBarButton}
                    buttonProps={{
                        styles: { root: { height: '100%' } },
                        iconProps: { iconName: 'Delete' },
                        disabled: context.isSubmitting,
                        onMouseDown: event => event.preventDefault(),
                    }}
                    dialog={DeleteDialog}
                    dialogProps={{
                        dialogContentProps: {
                            title: translate('file.pivot.note.delete.title'),
                            subText: translate('file.pivot.note.delete.subtitle'),
                        },
                        onDelete: () => {
                            context.setSubmitting(true)
                            dispatch({
                                type: NoteAction.Delete,
                                payload: axios.delete(`/note/${note.id}`),
                            })
                        },
                    }}
                />
            ),
        },
        {
            visible: !!note.id,
            key: 'private-note',
            iconProps: { iconName: context.values.isPrivate ? 'Lock' : 'Unlock' },
            isIconOnly: true,
            title: context.values.isPrivate ? translate('file.pivot.note.private.title') : translate('file.pivot.note.public.title'),
            disabled: context.isSubmitting || !isAuthor,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                ;(async () =>
                    context.setFieldValue(
                        'isPrivate',
                        note.id ? (await axios.post(`/note/${note.id}/toggle-private`)).data : !context.values.isPrivate,
                    ))()
            },
        },
        {
            visible: !!note.id,
            key: 'note-revisions',
            iconProps: { iconName: 'Rotate' },
            text: (note.revisionCount || 0).toString(),
            title: translate('file.pivot.note.revision.count', { count: note.revisionCount }),
        },
    ].filter(item => item.visible),
    farItems: [
        {
            visible: edit,
            key: 'save-note',
            iconProps: { iconName: 'Save' },
            iconOnly: true,
            title: translate('action.save'),
            disabled: context.isSubmitting,
            onMouseDown: event => event.preventDefault(),
            onClick: () => {
                context.submitForm()
            },
        },
        {
            visible: !edit,
            key: 'edit-note',
            iconProps: { iconName: 'Edit' },
            iconOnly: true,
            title: translate('action.edit'),
            onClick: setEdit,
        },
    ].filter(item => item.visible),
})

export function RevisionTitle(props: RevisionTitleProps): ReactElement {
    const { currentUser } = useUser()
    const { t: translate } = useTranslation()

    const author = props.author ? (props.author?.id === currentUser?.id ? 'uzelf' : props.author.fullName) : 'onbekend'

    return (
        <Separator styles={{ root: { flex: 1 } }}>
            {props.isNew ? (
                <Text variant="small">{translate('file.pivot.note.revision.title.new')}</Text>
            ) : (
                <>
                    <FormattedDateTime date={props.date || new Date()} />
                    <Text variant="small">{translate('file.pivot.note.revision.title.saved', { author: author })} </Text>
                </>
            )}
        </Separator>
    )
}

export function Content({ edit, setEdit }: ContentProps): ReactElement {
    const { t: translate } = useTranslation()
    const { values, setFieldValue, isSubmitting, submitForm } = useFormikContext<NoteMutationDto>()

    if (isSubmitting) {
        return <Loading label={translate('base.saving')} />
    } else if (edit) {
        return (
            <TextField
                value={values.content}
                onChange={(_event: FormEvent, newValue?: string) => setFieldValue('content', newValue)}
                onBlur={submitForm}
                styles={{
                    root: { margin: '-7px -1px 0px -1px' },
                    field: { lineHeight: 'normal' },
                }}
                multiline
                autoFocus
                resizable={false}
                rows={10}
            />
        )
    }

    return (
        <Text
            variant="medium"
            onClick={setEdit}
            styles={{
                root: {
                    height: '100%',
                    overflow: 'auto',
                    padding: '6px 8px 5px 8px',
                    margin: '-6px 0 1px 0',
                },
            }}
        >
            {nl2br(values.content)}
        </Text>
    )
}

function Container(props: ContainerProps): ReactElement {
    const axios = useAxios()
    const dispatch: Dispatch = useDispatch()
    const { currentUser } = useUser()
    const { t: translate } = useTranslation()

    const context = useFormikContext<NoteMutationDto>()
    const {
        note,
        editProps: [edit, { setTrue: setEdit }],
    } = props

    const isAuthor = useMemo<boolean>(() => note.createdByAuthor?.id === currentUser?.id, [note, currentUser])
    const commandBarItems = useMemo<CommandBarItems>(
        () => getCommandBarItems(context, dispatch, axios, translate, props, isAuthor),
        [context.values, props, isAuthor],
    )

    return (
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { minWidth: 240, maxWidth: 480 } }}>
            <StackItem>
                <PersonAvatar size={48} name={note.id ? note.createdByAuthor.fullName : currentUser?.fullName} color={'colorful'} />{' '}
                <Text variant={'small'}>
                    {note.id &&
                        translate('file.pivot.note.subtitle', {
                            datetime: formatFullDateTime(note.createdAt),
                        })}
                </Text>
                <RevisionTitle isNew={!note.id} date={note.updatedAt} author={note.updatedByAuthor} />
            </StackItem>
            <Stack verticalFill styles={{ root: { minHeight: 'calc(10em + 17px)', fontSize: 18 } }}>
                <Content edit={edit} setEdit={setEdit} />
            </Stack>
            <StackItem styles={{ root: { marginTop: '1px !important' } }}>
                <CommandBar styles={{ root: { padding: 0 } }} items={commandBarItems.items} farItems={commandBarItems.farItems} />
            </StackItem>
        </Stack>
    )
}

export default function Note(props: Props): ReactElement {
    const axios = useAxios()
    const editProps = useBoolean(!props.id)
    const dispatch: Dispatch = useDispatch()
    const { currentUser } = useUser()
    const currentFile = useSelector<Store, IFileDto>(state => state.files.current.data)

    return (
        <Formik<NoteMutationDto>
            initialValues={{
                content: props.content,
                authorId: currentUser?.id,
                fileId: currentFile?.id || props.file?.id,
                isPrivate: props.isPrivate,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={(values: NoteMutationDto, formikHelpers: FormikHelpers<NoteMutationDto>) => {
                editProps[1].setFalse()

                if (values.content !== props.content) {
                    return dispatch(
                        props.id
                            ? {
                                  type: NoteAction.Update,
                                  payload: axios.put(`/note/${props.id}`, values),
                              }
                            : {
                                  type: NoteAction.Add,
                                  payload: axios.post('/note', values),
                              },
                    )
                }

                formikHelpers.setSubmitting(false)

                return undefined
            }}
        >
            <Container note={props} editProps={editProps} />
        </Formik>
    )
}
